import React from "react";
import { Star, ShieldCheck } from "lucide-react";

export function StarRating({ value = 0, count, size = 14, showCount = true, className = "" }) {
  const rounded = Math.round(value);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <Star size={size} className="fill-amber-400 text-amber-400" />
      <span className="font-semibold text-slate-800">{value ? value.toFixed(1) : "Baru"}</span>
      {showCount && count != null && <span className="text-slate-400 text-xs">({count})</span>}
    </span>
  );
}

export function StarInput({ value, onChange, size = 32 }) {
  return (
    <div className="flex gap-1" data-testid="star-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`star-${n}`}
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110 active:scale-95"
        >
          <Star
            size={size}
            className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}
          />
        </button>
      ))}
    </div>
  );
}

const REP_STYLES = {
  Perunggu: "bg-amber-100 text-amber-800",
  Perak: "bg-slate-200 text-slate-700",
  Emas: "bg-yellow-100 text-yellow-700",
};

export function ReputationBadge({ level, className = "" }) {
  if (!level) return null;
  return (
    <span data-testid="reputation-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${REP_STYLES[level] || "bg-slate-100 text-slate-600"} ${className}`}>
      <ShieldCheck size={12} /> {level}
    </span>
  );
}

const THRESH = [["Perunggu", 1], ["Perak", 3], ["Emas", 10]];

export function nextRepInfo(completed = 0) {
  for (const [name, min] of THRESH) {
    if (completed < min) return { next: name, remaining: min - completed };
  }
  return null;
}

export function ReputationProgress({ completed = 0 }) {
  let prev = 0, nextMin = 1, nextName = "Perunggu", maxed = false;
  if (completed >= 10) { maxed = true; }
  else if (completed >= 3) { prev = 3; nextMin = 10; nextName = "Emas"; }
  else if (completed >= 1) { prev = 1; nextMin = 3; nextName = "Perak"; }
  const pct = maxed ? 100 : Math.min(100, Math.round(((completed - prev) / (nextMin - prev)) * 100));
  return (
    <div className="w-full max-w-[220px] mx-auto" data-testid="rep-progress">
      <p className="text-xs text-slate-500 mb-1">
        {maxed ? (
          <span className="font-semibold text-yellow-700">Level tertinggi tercapai: Emas</span>
        ) : (
          <><span className="font-bold text-slate-700">{nextMin - completed} pekerjaan</span> lagi menuju <span className="font-bold">{nextName}</span></>
        )}
      </p>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function RatingBreakdown({ breakdown, average, count }) {
  const total = count != null ? count : Object.values(breakdown || {}).reduce((a, b) => a + b, 0);
  if (!total) return null;
  return (
    <div className="w-full" data-testid="rating-breakdown">
      <div className="flex items-center gap-4 mb-3">
        <div className="text-center">
          <p className="text-3xl font-extrabold text-slate-900 leading-none">{Number(average || 0).toFixed(1)}</p>
          <div className="mt-1"><StarRating value={average} showCount={false} size={13} /></div>
          <p className="text-xs text-slate-400 mt-0.5">{total} ulasan</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const c = breakdown?.[star] || 0;
            const pct = total ? Math.round((c / total) * 100) : 0;
            return (
              <div key={star} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 text-slate-500 font-medium">{star}</span>
                <Star size={11} className="fill-amber-400 text-amber-400 shrink-0" />
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-6 text-right text-slate-400 tabular-nums">{c}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
